import React, { RefCallback, useEffect, useState } from "react";
import { FormControlLabel } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { DropDownCreatable } from "@/components/forms/inputs/dropDownCreateable";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import { requiredRule } from "@/components/tables/fields/fieldRules";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudCreatableSelectFieldProps {
  field: Pick<
    CrudModalField,
    "id" | "header" | "required" | "errorMessage" | "selectOptions"
  >;
  disabled?: boolean;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: unknown) => void;
}

const toDropDownDefaultValue = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return value === false ? "false" : value;
};

const CrudCreatableSelectField = ({
  field,
  disabled,
  validationTargetRef,
  onChange,
}: CrudCreatableSelectFieldProps) => {
  const name = field.id as string;
  const { control, getValues } = useFormContext();
  const formValue = useWatch({ control, name });
  const [preselectedValue, setPreselectedValue] = useState<unknown>(() =>
    getValues(name)
  );
  const [options, setOptions] = useState<any[]>([]);

  useEffect(() => {
    if (formValue) setPreselectedValue(formValue); // DropDownCreatable blanks the form on mount before its options exist; only a filled value may become the preselection
  }, [formValue]);

  useEffect(() => {
    setOptions(field.selectOptions); 
  }, []);

  const handleSelection = (
    selectedOption: any,
    setFormValue: (value: unknown) => void
  ) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      onChange?.(selectedOption.value);
      setFormValue(selectedOption.value);
      return;
    }
    setFormValue("");
  };

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={requiredRule(field.required, field.errorMessage)}
      validationTargetRef={validationTargetRef}
      className={styles.selectWrapper}
      render={({ field: { onChange: setFormValue, ref, ...rest }, error }) => (
        <FormControlLabel
          label={field.header}
          labelPlacement="top"
          sx={{
            margin: "0 ",
            width: "100%",
            alignItems: "start",
            fontFamily: "inherit !important",
          }}
          disableTypography
          control={
            <DropDownCreatable
              {...rest}
              ref={ref}
              options={options}
              name={field.id}
              defaultValue={toDropDownDefaultValue(preselectedValue)}
              isClearable={true}
              disabled={disabled}
              onChange={(selectedOption: any) =>
                handleSelection(selectedOption, setFormValue)
              }
              error={error}
              updateOptions={(newOption: any) =>
                setOptions([...options, newOption])
              }
            />
          }
        />
      )}
    />
  );
};

export default CrudCreatableSelectField;
